import { NavLink, useLocation } from 'react-router-dom'
import { Skeleton } from '@/components/Skeleton'
import { navIcons, type NavIconKey } from './navIcons'
import { APP_NAV_ITEMS } from './navItems'
import styles from './AppNavLinks.module.css'

// Icon + label pairing for a nav row. In collapsed mode CSS hides the
// label and the row's aria-label / title keep the accessible name.
export function RowInner({ icon, label }: { icon: NavIconKey; label: string }) {
  return (
    <>
      <span className={styles.icon} aria-hidden="true">
        {navIcons[icon]}
      </span>
      <span className={styles.label}>{label}</span>
    </>
  )
}

// A placeholder row matching a nav link's footprint, shown only while
// auth is still resolving (state === 'checking'). It reserves the final
// layout so the menu settles into the real state instead of flashing the
// signed-out menu first. Used by DrawerNavContent — the sidebar itself no
// longer draws anything auth-dependent (see below), so it never waits.
export function SkeletonRow({ width }: { width: string }) {
  return (
    <div className={styles.skeletonRow} aria-hidden="true">
      <Skeleton variant="rect" width={width} height="10px" />
    </div>
  )
}

type Props = {
  /** Desktop icon-rail mode: labels collapse to icon-only rows with a
   * tooltip. Only the sidebar sets this; the drawer is always expanded. */
  collapsed?: boolean
}

/**
 * The operational left rail (desktop sidebar only, and the sidebar exists only
 * inside AppShell — so this IS the workspace menu): the board row, then the four
 * catalogues. It draws APP_NAV_ITEMS, the ONE list every other surface draws
 * (owner, 2026-09-07) — the rail had its own shorter list until Home came off the
 * public one too, and then there was nothing left for a second list to say.
 *
 * ONE GROUP, AND NO MANAGE (owner, 2026-09-07). The rail used to end in a
 * "Manage" group carrying Workspace / Admin. Making the avatar dropdown read the
 * same on every surface (8684a339) put those two links in the top bar as well,
 * so on a desktop app screen the same two rows appeared twice a few hundred
 * pixels apart. The owner's answer was to keep the avatar menu as their single
 * home — §2.4 of the brief resolved the other way round — so the group is gone
 * from here rather than suppressed in the dropdown.
 *
 * That costs the rail nothing at any width: the sidebar renders only at ≥1024px,
 * exactly where AppShell's top bar carries PublicAccountMenu (Manage + Account);
 * below 1024px there is no sidebar at all and the phone drawer's own Manage
 * group is untouched. So this component no longer reads auth — every row it
 * draws is the same for every viewer.
 *
 * The brand wordmark, the outward links, and the account cluster live in the
 * persistent top bar, so this rail is purely "do the work"
 * (docs/ux/2026-07-18-nav-top-plus-sidebar.md §2, Option A). The phone drawer
 * renders DrawerNavContent over the SAME list, so the two widths of this one menu
 * cannot drift.
 */
export function AppNavLinks({ collapsed = false }: Props) {
  const { pathname } = useLocation()

  // In the collapsed rail the label is visually hidden, so the accessible
  // name + a native hover tooltip come from aria-label / title instead.
  const tip = (label: string) =>
    collapsed ? { 'aria-label': label, title: label } : {}

  return (
    <nav
      className={`${styles.nav} ${collapsed ? styles.collapsed : ''}`.trim()}
      aria-label="Main"
    >
      <ul className={styles.list}>
        {APP_NAV_ITEMS.map((item) => {
          // One predicate drives both the highlight and aria-current.
          // undefined = no custom matcher; fall back to NavLink's own
          // prefix matching for both.
          const active = item.isActive?.(pathname)
          return (
            <li key={item.href} className={styles.item}>
              <NavLink
                to={item.href}
                end={item.href === '/'}
                className={({ isActive: defaultActive }) =>
                  `${styles.link} ${(active ?? defaultActive) ? styles.linkActive : ''}`.trim()
                }
                aria-current={active === false ? false : 'page'}
                {...tip(item.label)}
              >
                <RowInner icon={item.icon} label={item.label} />
              </NavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
